import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Card, CardHeader, Collapse as ReactstrapCollapse } from 'reactstrap';
import './index.scss';

/**
 * Lightweight components that can be composed to create an accordion. The
 * accordion state and ARIA attributes are set outside of the components and
 * passed in as props.
 */

type AccordionProps = {
  className?: string;
  children: ReactNode;
};

function Accordion({ className, children }: AccordionProps): ReactElement {
  return (
    <Card className={clsx('chart-accordion d-flex flex-column mb-4', className)}>{children}</Card>
  );
}

type HeaderProps = {
  id: string;
  ariaExpanded: boolean; // The visibility of the collapse that this header controls
  ariaControls: string; // The ID of the collapse that this header controls
  onClick(): void;
  children: ReactNode;
};

function Header({ id, ariaExpanded, ariaControls, onClick, children }: HeaderProps): ReactElement {
  return (
    <CardHeader
      id={id}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      className="btn d-flex justify-content-between align-items-center cursor-pointer"
      onClick={onClick}
    >
      {children}
    </CardHeader>
  );
}

type HeaderIconButtonProps = {
  isOpen: boolean;
  onClick(): void;
};

function HeaderIconButton({ isOpen, onClick }: HeaderIconButtonProps): ReactElement {
  return (
    <button
      className="accordion-lite-header-icon-button"
      type="button"
      onClick={(e): void => {
        // Required to stop an element underneath, such as an Accordion header,
        // from also being clicked:
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        onClick();
      }}
    >
      <FontAwesomeIcon
        icon={isOpen ? 'compress-alt' : 'expand-alt'}
        className="ms-2 d-print-none"
      />
    </button>
  );
}

type CollapseControlButtonProps = {
  id: string;
  ariaExpanded: boolean; // The visibility of the collapse that this button controls
  ariaControls: string; // The ID of the collapse that this button controls
  onClick(): void;
  children: ReactNode;
};

function CollapseControlButton({
  id,
  ariaExpanded,
  ariaControls,
  onClick,
  children
}: CollapseControlButtonProps): ReactElement {
  return (
    <button
      id={id}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      className="d-print-none btn btn-outline-secondary btn-sm"
      type="button"
      onClick={(e): void => {
        // Required to stop an element underneath, such as an Accordion header,
        // from also being clicked:
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        onClick();
      }}
    >
      {children}
    </button>
  );
}

type CollapseProps = {
  id: string;
  ariaLabelledBy: string; // The ID of the button that controls this collapse
  isOpen: boolean;
  children: ReactNode;
};

function Collapse({ id, ariaLabelledBy, isOpen, children }: CollapseProps): ReactElement {
  return (
    <ReactstrapCollapse id={id} aria-labelledby={ariaLabelledBy} isOpen={isOpen}>
      {children}
    </ReactstrapCollapse>
  );
}

export default Object.assign(Accordion, {
  Header,
  HeaderIconButton,
  CollapseControlButton,
  Collapse
});
